import { Color, ShaderMaterial, Vector2 } from 'three';
import { BAIZE, BAIZE_DEEP } from '../art/palette.ts';

// The lighting of the board is printed, not rendered: light falloff and the shadows under
// pinned cards come out as a 45° halftone screen, like the photos in the gazette itself.

const HALFTONE = /* glsl */ `
  float halftone(float darkness, float cell) {
    vec2 p = gl_FragCoord.xy / cell;
    p = vec2(p.x + p.y, p.x - p.y) * 0.70710678;
    vec2 f = fract(p) - 0.5;
    float radius = sqrt(clamp(darkness, 0.0, 1.0)) * 0.64;
    float edge = 0.09;
    return 1.0 - smoothstep(radius - edge, radius + edge, length(f));
  }
`;

const WORLD_VERTEX = /* glsl */ `
  varying vec3 vWorld;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

export function feltMaterial(pixelRatio: number): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      uBase: { value: new Color(BAIZE) },
      uDeep: { value: new Color(BAIZE_DEEP) },
      uLight: { value: new Vector2(0, -1) },
      uRadius: { value: 9 },
      uCell: { value: 7 * pixelRatio },
    },
    vertexShader: WORLD_VERTEX,
    fragmentShader: /* glsl */ `
      uniform vec3 uBase;
      uniform vec3 uDeep;
      uniform vec2 uLight;
      uniform float uRadius;
      uniform float uCell;
      varying vec3 vWorld;
      ${HALFTONE}
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
      }
      void main() {
        float fibers = noise(vWorld.xy * 36.0) * 0.55 + noise(vWorld.xy * 110.0) * 0.45;
        vec3 color = uBase * (0.9 + fibers * 0.16);
        float falloff = smoothstep(0.3, 1.35, distance(vWorld.xy, uLight) / uRadius);
        color = mix(color, uDeep, halftone(falloff * 0.95, uCell) * 0.92);
        gl_FragColor = vec4(color, 1.0);
        #include <colorspace_fragment>
      }
    `,
  });
}

/** Soft rectangular shadow, printed as dots, for a card of `size` lifted by `uLift`. */
export function shadowMaterial(pixelRatio: number, size: Vector2): ShaderMaterial {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uColor: { value: new Color('#06140f') },
      uSize: { value: size.clone() },
      uLift: { value: 0 },
      uOpacity: { value: 1 },
      uCell: { value: 5 * pixelRatio },
    },
    vertexShader: WORLD_VERTEX,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform vec2 uSize;
      uniform float uLift;
      uniform float uOpacity;
      uniform float uCell;
      varying vec2 vUv;
      ${HALFTONE}
      float boxDistance(vec2 p, vec2 half_) {
        vec2 d = abs(p) - half_;
        return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
      }
      void main() {
        float blur = 0.06 + uLift * 0.35;
        vec2 extent = uSize * 0.5 + vec2(0.45);
        vec2 p = (vUv - 0.5) * 2.0 * extent;
        float d = boxDistance(p, uSize * 0.5 - vec2(0.04));
        float density = (1.0 - smoothstep(-blur, blur * 2.2, d)) * (0.78 - uLift * 0.18);
        float dotMask = halftone(density, uCell);
        gl_FragColor = vec4(uColor, dotMask * uOpacity * 0.85);
        #include <colorspace_fragment>
      }
    `,
  });
}
